import {
  Button,
  Checkbox,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from "@heroui/react";
import EditableChip from "@/components/common/EditableChip";
import HintTooltip from "@/components/common/HintTooltip";
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table";
import { ArrowUpRight, Ban, MoreVertical, RotateCcw, Trash2 } from "lucide-react";

import RowDragHandle from "@/components/table/RowDragHandle";
import InlineEditCell from "@/components/common/InlineEditCell";
import { formatAmount } from "@/lib/format";
import type { QuotationItem, QuotationItemInput } from "./api";

/**
 * 見積明細テーブルの列定義。
 *
 * 並び替えは行番号（見積書の並び）そのものなので、列ヘッダでの並び替えは持たない。
 * 順序は先頭列のハンドルをドラッグして変える。
 *
 * 編集の仕方は項目によって分けている:
 *
 * - **数量・単価・摘要・図番・品名・材質など** … セルの中で直接編集する。
 *   どれも明細自身が持つ項目で、互いに影響しないので 1 項目ずつ保存してよい
 * - **図番** … 製作対象の行でのみ編集できる。行の種類（製作対象かどうか）は
 *   起票時に決まり、あとから変えられない
 * - **金額** … 単価 × 数量の結果なので編集しない
 * - **状態** … 完了だけをデータに持ち、未入力・入力中は中身から導く
 * - **図面リンク** … 押すと図面を選んで紐付けるダイアログが開く
 *   （[[ItemDrawingLinkDialog]]）。図面タブからも張れるが、あちらは図面 1 枚を
 *   開いて明細を選ぶ形で向きが逆なので、行から入る口をここに置く
 *
 * **除外（`cancelled`）した行はどの列も編集できない**。客先が発注しなかった行の
 * 金額や数量を直せると、何を見積って何が落ちたのかが辿れなくなる（API も 400 で
 * 断る）。行はグレーにして残し、操作列のメニューから除外の解除と削除だけができる。
 * 並べ替えは除外した行でもできる（見積書の並びは見たままに保てたほうがよい）。
 */
export interface QuotationItemColumnHandlers {
  canWrite: boolean;
  /** 先頭・末尾で上下ボタンを無効にするための総行数。 */
  itemCount: number;
  /** まとめて削除する対象として選ばれている明細の id。 */
  selectedIds: Set<number>;
  /** 行の選択を切り替える。 */
  onToggleSelect: (item: QuotationItem) => void;
  /** その明細の見積画面（左に図面・右に工程ごとの金額）へ移る。 */
  onOpenEditor: (item: QuotationItem) => void;
  /**
   * 図面を足したり紐付けたりできるか。**`canWrite` とは別に持つ**。
   *
   * 受注済みの見積は中身を直せない（[[quotation_lock]]）が、**図面だけは別**。
   * 受注後に届く追図・差し替えは普通のことで、見積側に足した図面は案件からも
   * 見えるようにしてある（[[drawing_project_link]]）。
   */
  canLinkDrawing: boolean;
  /** 図面リンクの列を押したとき。図面を選んで紐付けるダイアログを開く。 */
  onOpenDrawingLink: (item: QuotationItem) => void;
  /** 明細の 1 項目だけ保存する。エラー文言を返すとセルの値は元に戻る。 */
  onSaveField: (
    item: QuotationItem,
    input: QuotationItemInput,
  ) => Promise<string | null>;
  /** 見積完了の切り替え。 */
  onToggleCompleted: (item: QuotationItem) => void;
  /** 除外（見積から外す）の切り替え。 */
  onToggleCancelled: (item: QuotationItem) => void;
  onMove: (item: QuotationItem, direction: -1 | 1) => void;
  onDelete: (item: QuotationItem) => void;
}

const helper = createColumnHelper<QuotationItem>();

/**
 * 明細の状態。**完了だけをデータに持ち、残りは中身から導く**。
 *
 * 未入力・入力中まで列として持つと、工程を消したのにフラグが残る類の食い違いが
 * 起きる。図面行は工程を積んだか、それ以外の行（運搬費・値引きなど）は単価を
 * 入れたかで「入力中」になる。
 */
function itemStatus(item: QuotationItem): {
  label: string;
  color: "default" | "primary" | "success";
} {
  if (item.completed) return { label: "完了", color: "success" };
  const started = item.priced_by_processes
    ? item.process_count > 0
    : Number(item.unit_price) !== 0;
  return started
    ? { label: "入力中", color: "primary" }
    : { label: "未入力", color: "default" };
}

export function buildQuotationItemColumns({
  canWrite,
  canLinkDrawing,
  itemCount,
  selectedIds,
  onToggleSelect,
  onOpenEditor,
  onOpenDrawingLink,
  onSaveField,
  onToggleCompleted,
  onToggleCancelled,
  onMove,
  onDelete,
}: QuotationItemColumnHandlers): ColumnDef<QuotationItem, never>[] {
  /**
   * その行を編集できるか。除外した行は権限があっても直せない。
   *
   * `InlineEditCell` は `canWrite` が false なら素のテキストを出すので、渡す値を
   * 行ごとに決めるだけでセルが読み取り専用になる。
   */
  const rowEditable = (item: QuotationItem) => canWrite && !item.cancelled;

  /**
   * 製作対象の行にだけある項目のセル。運搬費・値引きなどの行には無い情報なので
   * 出さない。
   */
  const drawingField = (
    item: QuotationItem,
    label: string,
    value: string | null,
    key: keyof QuotationItemInput,
  ) =>
    item.drawing_number == null ? (
      <span className="text-default-300">—</span>
    ) : (
      <InlineEditCell
        label={label}
        value={value}
        canWrite={rowEditable(item)}
        onSave={(v) => onSaveField(item, { [key]: v } as QuotationItemInput)}
      />
    );

  return [
    // 選択列。ヘッダではなく表の上の「すべて選択」で全行を選ぶ（ヘッダのセルは
    // 並び替えのボタンで埋まっており、中に押せるものを置けない）。
    ...(canWrite
      ? [
          helper.display({
            id: "select",
            header: "選択",
            size: 60,
            enableResizing: false,
            meta: { label: "選択", mandatory: true, pinned: true },
            cell: (ctx) => {
              const item = ctx.row.original;
              return (
                <Checkbox
                  size="sm"
                  isSelected={selectedIds.has(item.quotation_item_id)}
                  onValueChange={() => onToggleSelect(item)}
                  aria-label={`${item.sequence} 行目を選択`}
                  classNames={{ base: "m-0 p-0", label: "hidden" }}
                />
              );
            },
          }),
        ]
      : []),
    helper.accessor("sequence", {
      id: "sequence",
      header: "行",
      // 見積画面への入口をテキスト付きのボタンで置くぶん、行番号の列にしては
      // 広めに取る。固定列なので隠せず、入口が消えることもない。
      size: 165,
      meta: { label: "行番号", mandatory: true, pinned: true },
      cell: (ctx) => {
        const row = ctx.row.original;
        return (
          <RowDragHandle
            rowId={row.quotation_item_id}
            label={`${row.sequence} 行目`}
            isDisabled={!canWrite}
            canMoveUp={row.sequence > 1}
            canMoveDown={row.sequence < itemCount}
            onMove={(direction) => onMove(row, direction)}
          >
            <span className="min-w-4">{row.sequence}</span>
            {/* 見積画面への入口。行そのものを押して移る形にすると、セルの
                インライン編集と取り合いになるため、押す場所を 1 点に絞っている。
                工程を積めるのは製作対象の行だけなので、それ以外には出さない。
                除外した行は工程も直せないので入口も出さない。 */}
            {row.drawing_number != null && !row.cancelled && (
              <HintTooltip content="図面を見ながら工程ごとの金額を入れる">
                <Button
                  size="sm"
                  color="primary"
                  className="h-7 min-w-0 gap-1 px-2"
                  aria-label={`${row.sequence} 行目の見積を入力`}
                  // 鉛筆の形は「その場で直す」の印にしてあるので、画面が移る
                  // 入口にはそちらの印（右上向きの矢印）を添える（[[LinkCell]]）。
                  endContent={<ArrowUpRight size={14} />}
                  onPress={() => onOpenEditor(row)}
                >
                  見積入力
                </Button>
              </HintTooltip>
            )}
          </RowDragHandle>
        );
      },
    }),
    helper.display({
      id: "drawing_link",
      header: "図面リンク",
      size: 110,
      meta: { label: "図面リンク" },
      // 図面が付いていない行は見積（工程）を組めないので、明細一覧でも
      // 見つけられるようにしておく。押せばその場で紐付けられる。
      cell: (ctx) => {
        const item = ctx.row.original;
        // 運搬費・値引きなどの行には図面が付かないため、状態を出さない。
        if (item.drawing_number == null)
          return <span className="text-default-300">—</span>;
        const chip =
          item.drawing_id == null ? (
            // 除外した行の「紐付無し」は手を打つ必要がないので、黄色で呼ばない。
            <Chip
              size="sm"
              variant="flat"
              color={item.cancelled ? "default" : "warning"}
            >
              紐付無し
            </Chip>
          ) : (
            <Chip size="sm" variant="flat" color="success">
              紐付済
            </Chip>
          );
        // 除外した行は図面も張り替えられない（見積から外した行なので、図面を
        // 探す意味がない）。受注済みでも張り替えられるのはこの列だけ。
        return (
          <EditableChip
            label={`${item.sequence} 行目の図面を紐付け`}
            title="押すと図面を選んで紐付けられます"
            canWrite={canLinkDrawing && !item.cancelled}
            onPress={() => onOpenDrawingLink(item)}
          >
            {chip}
          </EditableChip>
        );
      },
    }),
    helper.display({
      id: "status",
      header: "状態",
      size: 100,
      meta: { label: "状態" },
      cell: (ctx) => {
        const item = ctx.row.original;
        // 除外した行に見積の進み具合を出しても意味がない（見積る相手がもう
        // いない）。解除は操作列のメニューから行うので、ここは表示だけ。
        if (item.cancelled)
          return (
            <Chip size="sm" variant="flat" color="default">
              除外
            </Chip>
          );
        const { label, color } = itemStatus(item);
        const chip = (
          <Chip size="sm" variant="flat" color={color}>
            {label}
          </Chip>
        );
        // 押すと完了を切り替える。片道にすると、間違えて完了にしたときに
        // 戻せなくなる。
        return (
          <EditableChip
            label={
              item.completed
                ? `${item.sequence} 行目の見積完了を取り消す`
                : `${item.sequence} 行目を見積完了にする`
            }
            title={
              item.completed ? "押すと見積完了を取り消します" : "押すと見積完了にします"
            }
            canWrite={canWrite}
            onPress={() => onToggleCompleted(item)}
          >
            {chip}
          </EditableChip>
        );
      },
    }),
    helper.accessor("drawing_number", {
      id: "drawing_number",
      header: "図番",
      size: 170,
      meta: { label: "図番" },
      // 図番は明細が持つので、他に影響せずその場で打ち直せる。
      cell: (ctx) =>
        drawingField(ctx.row.original, "図番", ctx.getValue(), "drawing_number"),
    }),
    helper.accessor("item_name", {
      id: "item_name",
      header: "品名",
      size: 260,
      meta: { label: "品名" },
      // 品名はどの行も明細自身が持つ。
      cell: (ctx) => (
        <InlineEditCell
          label="品名"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { item_name: v })}
        />
      ),
    }),
    helper.accessor("client_drawing_number_raw", {
      id: "client_drawing_number_raw",
      header: "客先図番表記",
      size: 160,
      meta: { label: "客先の図番表記" },
      cell: (ctx) =>
        drawingField(
          ctx.row.original,
          "客先の図番表記",
          ctx.getValue(),
          "client_drawing_number_raw",
        ),
    }),
    helper.accessor("client_drawing_sheet_no", {
      id: "client_drawing_sheet_no",
      header: "客先図面番号",
      size: 160,
      meta: { label: "客先の図面番号" },
      // 平田機工の「図面番号」。図番（品目コード）とは別の番号で、客先が図面を
      // 指すときに使う。
      cell: (ctx) => (
        <InlineEditCell
          label="客先の図面番号"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { client_drawing_sheet_no: v })}
        />
      ),
    }),
    helper.accessor("unit_price", {
      id: "unit_price",
      header: "単価",
      size: 130,
      meta: { label: "単価", numeric: true },
      cell: (ctx) =>
        // 製作対象の行の単価は工程別見積の合算なので、ここでは直接編集させない
        // （編集できるように見せると、工程と食い違う値を入れられてしまう）。
        ctx.row.original.priced_by_processes ? (
          <span
            className="text-default-500"
            title={
              ctx.row.original.process_count > 0
                ? `工程 ${ctx.row.original.process_count} 件の合計`
                : "工程が未入力です"
            }
          >
            {formatAmount(ctx.getValue())}
          </span>
        ) : (
          // 編集するのは生の値、見せるのは 3 桁区切り。
          <InlineEditCell
            label="単価"
            type="number"
            numeric
            value={ctx.getValue()}
            displayValue={formatAmount(ctx.getValue())}
            canWrite={rowEditable(ctx.row.original)}
            onSave={(v) => onSaveField(ctx.row.original, { unit_price: v ?? "0" })}
          />
        ),
    }),
    helper.accessor("amount", {
      id: "amount",
      header: "金額",
      size: 140,
      meta: { label: "金額", numeric: true },
      cell: (ctx) => formatAmount(ctx.getValue()),
    }),
    helper.accessor("material", {
      id: "material",
      header: "材質",
      size: 150,
      meta: { label: "材質" },
      cell: (ctx) => drawingField(ctx.row.original, "材質", ctx.getValue(), "material"),
    }),
    helper.accessor("surface_treatment", {
      id: "surface_treatment",
      header: "表面処理",
      size: 200,
      meta: { label: "表面処理" },
      cell: (ctx) =>
        drawingField(ctx.row.original, "表面処理", ctx.getValue(), "surface_treatment"),
    }),
    helper.accessor("maker_name", {
      id: "maker_name",
      header: "メーカー",
      size: 140,
      meta: { label: "メーカー名" },
      cell: (ctx) => (
        <InlineEditCell
          label="メーカー名"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { maker_name: v })}
        />
      ),
    }),
    helper.accessor("maker_model", {
      id: "maker_model",
      header: "メーカー型式",
      size: 160,
      meta: { label: "メーカー型式" },
      cell: (ctx) => (
        <InlineEditCell
          label="メーカー型式"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { maker_model: v })}
        />
      ),
    }),
    helper.accessor("client_item_code", {
      id: "client_item_code",
      header: "客先品目コード",
      size: 200,
      meta: { label: "客先の品目コード" },
      cell: (ctx) => (
        <InlineEditCell
          label="客先の品目コード"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { client_item_code: v })}
        />
      ),
    }),
    helper.accessor("client_unit_no", {
      id: "client_unit_no",
      header: "ユニットNo",
      size: 110,
      meta: { label: "客先のユニットNo" },
      cell: (ctx) => (
        <InlineEditCell
          label="客先のユニットNo"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { client_unit_no: v })}
        />
      ),
    }),
    helper.accessor("client_bom_no", {
      id: "client_bom_no",
      header: "部品表No",
      size: 110,
      meta: { label: "客先の部品表No" },
      cell: (ctx) => (
        <InlineEditCell
          label="客先の部品表No"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { client_bom_no: v })}
        />
      ),
    }),
    helper.accessor("client_order_numbers", {
      id: "client_order_numbers",
      header: "客先購番",
      size: 200,
      meta: { label: "客先購番" },
      // この行に畳み込んだ購番。セット品は客先がセット 1 本ごとに購番を切るので
      // 複数本並び、**その本数がセット数**になる。**直せない**（紙の行そのものの
      // 記録）。案件明細と同じ列（[[itemColumns]]）。
      // 値の型は列定義全体で never に揃えているので、行から取り出す。
      cell: (ctx) => {
        const numbers = ctx.row.original.client_order_numbers;
        if (numbers.length === 0) return <span className="text-default-400">—</span>;
        return <span className="tabular-nums">{numbers.join(" / ")}</span>;
      },
    }),
    helper.accessor("quantity", {
      id: "quantity",
      header: "数量",
      size: 90,
      meta: { label: "数量", numeric: true },
      cell: (ctx) => (
        <InlineEditCell
          label="数量"
          type="number"
          numeric
          value={String(ctx.getValue())}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { quantity: Number(v ?? 0) })}
        />
      ),
    }),
    helper.accessor("unit", {
      id: "unit",
      header: "単位",
      size: 80,
      meta: { label: "単位" },
      cell: (ctx) => (
        <InlineEditCell
          label="単位"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { unit: v })}
        />
      ),
    }),
    helper.accessor("answered_quantity", {
      id: "answered_quantity",
      header: "回答数量",
      size: 100,
      meta: { label: "回答数量", numeric: true },
      // 空なら希望数量どおりで回答したことを表す。ロット都合で変えるときだけ入れる。
      cell: (ctx) => (
        <InlineEditCell
          label="回答数量"
          type="number"
          numeric
          value={ctx.getValue() == null ? null : String(ctx.getValue())}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) =>
            onSaveField(ctx.row.original, {
              answered_quantity: v == null ? null : Number(v),
            })
          }
        />
      ),
    }),
    helper.accessor("requested_deadline", {
      id: "requested_deadline",
      header: "希望納期",
      size: 130,
      meta: { label: "客先の希望納期" },
      cell: (ctx) => (
        <InlineEditCell
          label="客先の希望納期"
          type="date"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { requested_deadline: v })}
        />
      ),
    }),
    helper.accessor("answered_deadline", {
      id: "answered_deadline",
      header: "回答納期",
      size: 130,
      meta: { label: "回答納期" },
      // 空なら希望納期どおりで回答したことを表す。
      cell: (ctx) => (
        <InlineEditCell
          label="回答納期"
          type="date"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { answered_deadline: v })}
        />
      ),
    }),
    helper.accessor("target_unit_price", {
      id: "target_unit_price",
      header: "目標単価",
      size: 130,
      meta: { label: "客先の目標単価", numeric: true },
      // 客先が提示してきた単価。自社の見積単価ではないので金額には効かない。
      cell: (ctx) => (
        <InlineEditCell
          label="客先の目標単価"
          type="number"
          numeric
          value={ctx.getValue()}
          displayValue={ctx.getValue() ? formatAmount(ctx.getValue()!) : null}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { target_unit_price: v })}
        />
      ),
    }),
    helper.accessor("note", {
      id: "note",
      header: "摘要",
      size: 220,
      meta: { label: "摘要" },
      cell: (ctx) => (
        <InlineEditCell
          label="摘要"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { note: v })}
        />
      ),
    }),
    helper.accessor("internal_note", {
      id: "internal_note",
      header: "社内備考",
      size: 220,
      meta: { label: "社内備考" },
      // 客先向けの摘要とは別の列にしてある（社内メモが回答に混ざらないように）。
      cell: (ctx) => (
        <InlineEditCell
          label="社内備考"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { internal_note: v })}
        />
      ),
    }),
    helper.accessor("client_question", {
      id: "client_question",
      header: "客先からの質問",
      size: 240,
      meta: { label: "客先からの質問" },
      // 依頼ファイル（京セラの見積回答 CSV）の「質問a」。客先が書いてきた問いを
      // そのまま持つので、直すことは普通ないが、誤記は直せるようにしておく。
      //
      // **質問と回答は既定で表示する**。隠しておくと、質問が来ていることに
      // 気づかないまま回答ファイルを送り、回答欄が空のまま客先に出てしまう。
      cell: (ctx) => (
        <InlineEditCell
          label="客先からの質問"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { client_question: v })}
        />
      ),
    }),
    helper.accessor("question_answer", {
      id: "question_answer",
      header: "質問への回答",
      size: 240,
      meta: { label: "質問への回答" },
      // ここに入れた文章が回答ファイルの「質問aへの回答」として客先に出る。
      cell: (ctx) => (
        <InlineEditCell
          label="質問への回答"
          value={ctx.getValue()}
          canWrite={rowEditable(ctx.row.original)}
          onSave={(v) => onSaveField(ctx.row.original, { question_answer: v })}
        />
      ),
    }),
    helper.display({
      id: "actions",
      header: "操作",
      size: 70,
      enableResizing: false,
      meta: { label: "操作", mandatory: true },
      // 行に対する操作はここのメニューに集める。除外した行で押せるのは
      // 「除外を解除」と「削除」だけで、他の列はどれも編集できない。
      cell: (ctx) => {
        const item = ctx.row.original;
        if (!canWrite) {
          return (
            <HintTooltip content="リーダー以上の権限が必要です">
              {/* 無効なボタンはイベントを出さないため、Tooltip 用に包む。 */}
              <span className="inline-flex">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  isDisabled
                  aria-label={`${item.sequence} 行目の操作`}
                >
                  <MoreVertical size={18} />
                </Button>
              </span>
            </HintTooltip>
          );
        }
        return (
          <Dropdown placement="bottom-end">
            <DropdownTrigger>
              <Button
                isIconOnly
                size="sm"
                variant="light"
                aria-label={`${item.sequence} 行目の操作`}
              >
                <MoreVertical size={18} className="text-brand-500" />
              </Button>
            </DropdownTrigger>
            <DropdownMenu
              aria-label={`${item.sequence} 行目の操作`}
              onAction={(key) => {
                if (key === "cancel") onToggleCancelled(item);
                if (key === "delete") onDelete(item);
              }}
            >
              <DropdownItem
                key="cancel"
                startContent={
                  item.cancelled ? <RotateCcw size={18} /> : <Ban size={18} />
                }
                description={
                  item.cancelled ? "この行を見積に戻す" : "行は残したまま見積から外す"
                }
              >
                {item.cancelled ? "除外を解除" : "除外する"}
              </DropdownItem>
              <DropdownItem
                key="delete"
                className="text-danger"
                color="danger"
                startContent={<Trash2 size={18} />}
              >
                削除
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        );
      },
    }),
  ] as ColumnDef<QuotationItem, never>[];
}

/** 列 id の一覧（設定の永続化で「未知のキー」を捨てるのに使う）。 */
export const quotationItemColumnIds = [
  "select",
  "sequence",
  "drawing_link",
  "status",
  "drawing_number",
  "item_name",
  "client_drawing_number_raw",
  "client_drawing_sheet_no",
  "unit_price",
  "amount",
  "material",
  "surface_treatment",
  "maker_name",
  "maker_model",
  "client_item_code",
  "client_unit_no",
  "client_bom_no",
  "client_order_numbers",
  "quantity",
  "unit",
  "answered_quantity",
  "requested_deadline",
  "answered_deadline",
  "target_unit_price",
  "note",
  "internal_note",
  "client_question",
  "question_answer",
  "actions",
];

/**
 * 既定では非表示にする列。
 *
 * 客先の依頼フォーマットに合わせて項目を増やしてあるが、全部出すと表が横に
 * 伸びすぎて見積を組む作業に邪魔になる。客先ごとに要る項目が違う
 * （京セラはユニットNo・部品表No、平田機工は品目コード）ので、使う人が
 * 表示列の設定で出す。
 */
export const quotationItemDefaultHiddenColumnIds = [
  // 取り込んだ直後は図番と同じ値なので既定では隠す（図番を画面で直したときに、
  // 客先が何と書いてきたかを見るための列）。
  "client_drawing_number_raw",
  // 平田機工の依頼にしか無い項目なので、要る人が表示列の設定で出す。
  "client_drawing_sheet_no",
  "surface_treatment",
  "maker_name",
  "maker_model",
  "client_item_code",
  "client_unit_no",
  "client_bom_no",
  // セット品の見積でしか値が入らない列なので、案件明細と同じく既定では隠す。
  "client_order_numbers",
  "answered_quantity",
  "target_unit_price",
  "internal_note",
];

/** 左に固定する列の id。 */
export const quotationItemPinnedColumnIds = ["select", "sequence"];
